import { Inject, Injectable, Renderer2, RendererFactory2 } from '@angular/core';
import { DOCUMENT } from '@core/injection-tokens';

import { AppConfigService } from '../app-config/app-config.service';

declare let JBFELoggerSetup: Function;
declare let logOtherEvent: Function;

@Injectable({
  providedIn: 'root',
})
export class FrontEndLoggerService {
  private renderer: Renderer2;
  private loggerLoaded = false;
  private pendingLogs: { type: string; data: any }[] = [];
  constructor(
    @Inject(DOCUMENT) private document: Document,
    private appConfigService: AppConfigService,
    private rendererFactory: RendererFactory2,
  ) {
    this.init();
  }

  init() {
    if (
      this.appConfigService.isFeLoggerEnabled &&
      this.appConfigService.feLoggerUrl
    ) {
      this.renderer = this.rendererFactory.createRenderer(null, null);

      this.setupLogger();
    } else if (!this.appConfigService.isFeLoggerEnabled) {
      console.log('Logger service: configured off');
    } else if (!this.appConfigService.feLoggerUrl) {
      console.log('Logger service: logger not provided');
    }
  }

  loadLogger(
    src: string,
  ): Promise<{ src: string; loaded: boolean; status: string; error? }> {
    const script = this.renderer.createElement('script');
    script.src = src;
    this.renderer.appendChild(this.document.head, script);
    return new Promise((resolve, reject) => {
      script.onload = () => {
        resolve({
          src,
          loaded: true,
          status: 'Loaded',
        });
      };
      script.onerror = (error: any) =>
        reject({
          src,
          loaded: false,
          status: 'Error',
          error,
        });
    });
  }

  setupLogger() {
    this.loadLogger(this.appConfigService.feLoggerUrl).then(
      () => {
        this.loggerLoaded = true;
        // see https://github.com/jetblueairways/cb-logger-frontend/tree/main for detail
        try {
          const logData = {
            applicationInfo: {
              id: 'DotAngular',
              version: this.appConfigService.buildId,
            },
            loggerSettings: {
              captureError: true,
              capturePageLoad: true,
              captureClick: false,
              enableLoggingToService: true,
              batchMessages: true,
              enableConsoleLogs: !this.appConfigService.isProdEnv,
              performanceConfig: {
                captureResource: false,
                capturePaint: false,
                captureNavigation: false,
              },
            },
            environment: this.appConfigService.environment,
          };
          const homepagePaths = ['/', '/es', '/fr'];
          if (homepagePaths.includes(this.document.location.pathname)) {
            logData['additionalData'] = {
              homepage: 'DotAngular',
            };
          }
          JBFELoggerSetup(logData);
          // flush any logs that were collected while logger was loading
          this.flushPendingLogs();
        } catch (error) {
          console.warn('FE Logger: Error in initializing logger', error);
        }
      },
      result => {
        console.warn(
          `FE Logger: Unable to load logger from ${result.src}`,
          result.error,
        );
      },
    );
  }

  private flushPendingLogs() {
    if (!this.loggerLoaded || !this.pendingLogs.length) {
      return;
    }

    try {
      // send pending logs in order
      this.pendingLogs.forEach(entry => {
        try {
          logOtherEvent(entry.type, entry.data);
        } catch (e) {
          console.warn('FE Logger: failed to send pending log', e);
        }
      });
    } finally {
      this.pendingLogs = [];
    }
  }

  logError(data) {
    if (!this.loggerLoaded) {
      this.pendingLogs.push({ type: 'error', data });
      return;
    }
    logOtherEvent('error', data);
  }

  logData(data) {
    if (!this.loggerLoaded) {
      this.pendingLogs.push({ type: 'unknown', data });
      return;
    }
    logOtherEvent('unknown', data);
  }

  logPerformance(data) {
    if (!this.loggerLoaded) {
      this.pendingLogs.push({ type: 'performance', data });
      return;
    }
    try {
      logOtherEvent('performance', data);
    } catch (e) {
      console.warn('FE Logger: unable to log performance metric', e);
    }
  }
}
